import { Component } from 'react'
import api from '../api/client'
import { colors, font, text as tipo } from '../design/tokens'

/**
 * Red de seguridad de la aplicación.
 *
 * Sin esto, un error en cualquier pantalla desmonta TODO React y el usuario
 * queda mirando una pantalla en blanco: no sabe qué pasó, no puede seguir, y
 * nosotros no nos enteramos nunca. Pasó el 5 ago 2026 en el depósito central
 * (pantalla en blanco al seleccionar remitos) y no hubo forma de diagnosticarlo
 * a distancia — el error existía pero no dejaba rastro en ningún lado.
 *
 * Ahora:
 *  · el usuario ve qué pasó y puede reintentar sin perder la sesión;
 *  · el error VIAJA al servidor con la pantalla, el navegador y el detalle
 *    técnico, así se arregla sin depender de que alguien saque una foto de la
 *    consola.
 *
 * El reporte nunca puede romper más de lo que ya está roto: si falla el envío,
 * se ignora en silencio.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { error: null, detalle: null, enviado: false }
  }

  static getDerivedStateFromError(error) {
    return { error }
  }

  componentDidCatch(error, info) {
    const detalle = {
      mensaje: String(error?.message || error),
      stack: String(error?.stack || '').slice(0, 4000),
      componente: String(info?.componentStack || '').slice(0, 2000),
      pantalla: window.location.pathname + window.location.search,
      navegador: navigator.userAgent,
      pantalla_tamano: `${window.screen?.width}x${window.screen?.height}`,
    }
    this.setState({ detalle })

    // Aviso al servidor. Si no llega, no pasa nada: el usuario ya tiene su
    // mensaje y el botón de reintentar.
    api.post('/errores-frontend', detalle)
      .then(() => this.setState({ enviado: true }))
      .catch(() => {})
  }

  render() {
    if (!this.state.error) return this.props.children

    const d = this.state.detalle
    return (
      <div style={st.wrap}>
        <div style={st.card}>
          <div style={st.titulo}>Algo falló en esta pantalla</div>
          <p style={st.texto}>
            No es culpa de lo que estabas haciendo. La pantalla se detuvo, pero tu
            sesión sigue abierta y no se perdió nada de lo que ya estaba guardado.
          </p>
          <div style={st.acciones}>
            <button style={st.btnPrimario} onClick={() => this.setState({ error: null, detalle: null })}>
              Reintentar
            </button>
            <button style={st.btnSec} onClick={() => window.location.assign('/dashboard')}>
              Ir al inicio
            </button>
          </div>
          <p style={st.aviso}>
            {this.state.enviado
              ? '✓ El problema ya se reportó solo. No hace falta que avises.'
              : 'Si se repite, avisá en qué pantalla pasó.'}
          </p>
          {d && (
            <details style={st.det}>
              <summary style={st.sum}>Detalle técnico</summary>
              <pre style={st.pre}>{d.mensaje}{'\n\n'}{d.stack}</pre>
            </details>
          )}
        </div>
      </div>
    )
  }
}

const st = {
  wrap: { display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '70vh', padding: 24, fontFamily: font.sans },
  card: { maxWidth: 560, width: '100%', background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 10, padding: 24 },
  titulo: { fontSize: 18, fontWeight: 700, color: colors.text, marginBottom: 8 },
  texto: { fontSize: tipo.body, color: colors.textSec, lineHeight: 1.5, margin: '0 0 16px' },
  acciones: { display: 'flex', gap: 8 },
  btnPrimario: { background: '#1e40af', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 14px', fontSize: tipo.body, cursor: 'pointer', fontFamily: font.sans },
  btnSec: { background: colors.bg, color: colors.textStrong, border: `1px solid ${colors.border}`, borderRadius: 6, padding: '8px 14px', fontSize: tipo.body, cursor: 'pointer', fontFamily: font.sans },
  aviso: { fontSize: tipo.sec, color: colors.textMeta, margin: '14px 0 0' },
  det: { marginTop: 14 },
  sum: { fontSize: tipo.sec, color: colors.textSec, cursor: 'pointer' },
  pre: { fontSize: 11, fontFamily: font.mono, background: colors.bgSoft, color: colors.textSec, padding: 10, borderRadius: 6, overflow: 'auto', maxHeight: 220, whiteSpace: 'pre-wrap' },
}
